import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../utils/cn';

const isPlainObject = (value) => (
  value != null && typeof value === 'object' && !Array.isArray(value)
);

const isPrimitive = (value) => (
  value == null
  || typeof value === 'string'
  || typeof value === 'number'
  || typeof value === 'boolean'
);

const formatPrimitive = (value, t) => {
  if (value === null || value === undefined) return t('common.emDash');
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');
  if (typeof value === 'number') return String(value);
  const text = String(value);
  return text.trim() ? text : t('common.emDash');
};

const shouldOmitKey = (key, omitKeys) => {
  if (!omitKeys?.length) return false;
  const normalized = String(key).toLowerCase();
  return omitKeys.some((omit) => String(omit).toLowerCase() === normalized);
};

const stripOmittedKeys = (value, omitKeys) => {
  if (!omitKeys?.length) return value;
  if (Array.isArray(value)) {
    return value.map((item) => stripOmittedKeys(item, omitKeys));
  }
  if (!isPlainObject(value)) return value;

  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !shouldOmitKey(key, omitKeys))
      .map(([key, nested]) => [key, stripOmittedKeys(nested, omitKeys)]),
  );
};

const isSkillsKey = (key) => String(key ?? '').toLowerCase() === 'skills';
const isExperienceKey = (key) => String(key ?? '').toLowerCase() === 'experience';

const prepareNestedValue = (key, nested) => {
  if (isExperienceKey(key) && Array.isArray(nested)) {
    return nested.length > 0 ? [nested[0]] : [];
  }
  return nested;
};

const formatListItem = (item, t) => {
  if (isPrimitive(item)) return formatPrimitive(item, t);
  if (isPlainObject(item)) {
    const label = item.name ?? item.title ?? item.skill ?? item.label ?? item.text;
    if (label != null && String(label).trim()) return String(label).trim();
    return Object.values(item)
      .filter(isPrimitive)
      .map((value) => formatPrimitive(value, t))
      .filter((value) => value !== t('common.emDash'))
      .join(' ');
  }
  return String(item);
};

const formatAsCommaList = (value, t) => {
  if (!Array.isArray(value) || value.length === 0) return t('common.emDash');
  return value
    .map((item) => formatListItem(item, t))
    .map((item) => String(item ?? '').trim())
    .filter(Boolean)
    .join(', ') || t('common.emDash');
};

const ValueText = ({ value }) => {
  const { t } = useTranslation();
  const text = formatPrimitive(value, t);

  if (typeof value === 'string' && /^https?:\/\//i.test(value)) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="text-primary underline-offset-2 hover:underline break-all"
      >
        {text}
      </a>
    );
  }

  return <span className="break-all whitespace-pre-wrap">{text}</span>;
};

const FieldRows = ({ entries }) => {
  const { t } = useTranslation();
  if (!entries.length) return null;

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <table className="w-full table-fixed text-sm">
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key} className="border-b border-border last:border-b-0 align-top">
              <th className="w-[40%] bg-muted/40 px-3 py-2 text-left font-medium text-foreground break-all">
                {key}
              </th>
              <td className="px-3 py-2 text-foreground">
                {Array.isArray(value) ? (
                  <span className="break-all whitespace-pre-wrap">
                    {formatAsCommaList(value, t)}
                  </span>
                ) : (
                  <ValueText value={value} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SectionTitle = ({ title, level }) => {
  const className = cn(
    'font-semibold text-foreground break-all',
    level === 0 && 'text-xl',
    level === 1 && 'text-lg',
    level >= 2 && 'text-base',
  );

  if (level === 0) return <h4 className={className}>{title}</h4>;
  if (level === 1) return <h5 className={className}>{title}</h5>;
  return <h6 className={className}>{title}</h6>;
};

const JsonNode = ({ value, title, level = 0 }) => {
  const { t } = useTranslation();

  if (isPrimitive(value)) {
    return (
      <div className="space-y-2">
        {title ? <SectionTitle title={title} level={level} /> : null}
        <div className="rounded-md border border-border px-3 py-2 text-sm">
          <ValueText value={value} />
        </div>
      </div>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <div className="space-y-2">
          {title ? <SectionTitle title={title} level={level} /> : null}
          <p className="text-sm text-muted-foreground">{t('common.emDash')}</p>
        </div>
      );
    }

    if (isSkillsKey(title) || value.every(isPrimitive)) {
      return (
        <div className="space-y-2">
          {title ? <SectionTitle title={title} level={level} /> : null}
          <FieldRows entries={[[title || t('debug.value'), formatAsCommaList(value, t)]]} />
        </div>
      );
    }

    const items = isExperienceKey(title) ? value.slice(0, 1) : value;

    return (
      <div className="space-y-3">
        {title ? <SectionTitle title={title} level={level} /> : null}
        <div className="space-y-4">
          {items.map((item, index) => (
            <JsonNode
              key={`${title || 'item'}-${index}`}
              value={item}
              title={isExperienceKey(title) ? null : t('debug.itemIndex', { index: index + 1 })}
              level={level + 1}
            />
          ))}
        </div>
      </div>
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    const primitiveEntries = [];
    const complexEntries = [];

    entries.forEach(([key, nested]) => {
      const value = prepareNestedValue(key, nested);

      if (isSkillsKey(key) && Array.isArray(value)) {
        primitiveEntries.push([key, formatAsCommaList(value, t)]);
        return;
      }
      if (isPrimitive(value) || (Array.isArray(value) && value.every(isPrimitive))) {
        primitiveEntries.push([key, value]);
      } else {
        complexEntries.push([key, value]);
      }
    });

    return (
      <div className="space-y-3">
        {title ? <SectionTitle title={title} level={level} /> : null}
        {primitiveEntries.length > 0 && <FieldRows entries={primitiveEntries} />}
        {complexEntries.map(([key, nested]) => (
          <JsonNode
            key={key}
            value={nested}
            title={key}
            level={title ? level + 1 : level}
          />
        ))}
      </div>
    );
  }

  return null;
};

const JsonDataTable = ({ data, emptyLabel, omitKeys = [] }) => {
  const { t } = useTranslation();
  const cleaned = useMemo(
    () => stripOmittedKeys(data, omitKeys),
    [data, omitKeys],
  );

  if (cleaned == null) {
    return (
      <p className="text-sm text-muted-foreground">
        {emptyLabel || t('debug.noData')}
      </p>
    );
  }

  if (isPlainObject(cleaned) && Object.keys(cleaned).length === 0) {
    return <p className="text-sm text-muted-foreground">{t('debug.emptyObject')}</p>;
  }

  return <JsonNode value={cleaned} level={0} />;
};

export default JsonDataTable;
